import React, { useMemo, useState } from "react";
import "../styles/Careers.css";

const jobs = [
  {
    id: 1,
    title: "React JS Developer",
    department: "Engineering",
    location: "Hyderabad, India",
    type: "Full Time",
    experience: "0–2 Years",
    skills: ["React.js", "JavaScript", "HTML", "CSS", "Git"],
    description:
      "We are looking for a passionate React JS Developer to build modern, scalable and user-friendly web applications.",
    responsibilities: [
      "Develop responsive web applications using React.js",
      "Build reusable and maintainable React components",
      "Integrate REST APIs",
      "Collaborate with UI/UX designers and backend developers",
      "Debug and optimize applications",
      "Write clean and maintainable code",
    ],
  },
  {
    id: 2,
    title: "Java Developer",
    department: "Engineering",
    location: "Hyderabad, India",
    type: "Full Time",
    experience: "0–2 Years",
    skills: ["Java", "Spring Boot", "SQL", "REST API", "Git"],
    description:
      "Join our engineering team and work on scalable backend applications using Java and Spring Boot.",
    responsibilities: [
      "Develop backend applications using Java",
      "Create RESTful APIs",
      "Work with relational databases",
      "Write clean and reusable code",
      "Participate in code reviews",
      "Troubleshoot application issues",
    ],
  },
  {
    id: 3,
    title: "Data Analyst",
    department: "Data & Analytics",
    location: "Hyderabad, India",
    type: "Full Time",
    experience: "0–2 Years",
    skills: ["SQL", "Excel", "Power BI", "Python", "Data Analysis"],
    description:
      "We are looking for a Data Analyst who can transform business data into meaningful insights.",
    responsibilities: [
      "Analyze business datasets",
      "Create reports and dashboards",
      "Write SQL queries",
      "Identify trends and patterns",
      "Prepare data for analysis",
      "Present insights to stakeholders",
    ],
  },
  {
    id: 4,
    title: "UI/UX Designer",
    department: "Design",
    location: "Hyderabad, India",
    type: "Full Time",
    experience: "0–2 Years",
    skills: ["Figma", "UI Design", "UX Design", "Prototyping"],
    description:
      "Create beautiful, intuitive and engaging digital experiences for our customers and products.",
    responsibilities: [
      "Design web and mobile interfaces",
      "Create wireframes and prototypes",
      "Work with developers",
      "Conduct design research",
      "Create design systems",
      "Improve overall user experience",
    ],
  },
  {
    id: 5,
    title: "Digital Marketing Executive",
    department: "Marketing",
    location: "Hyderabad, India",
    type: "Full Time",
    experience: "0–2 Years",
    skills: ["SEO", "Social Media", "Google Ads", "Content"],
    description:
      "Help us grow our digital presence through creative campaigns and data-driven marketing strategies.",
    responsibilities: [
      "Manage social media campaigns",
      "Support SEO activities",
      "Create marketing content",
      "Analyze campaign performance",
      "Assist with paid advertising campaigns",
      "Research market trends",
    ],
  },
];

const benefits = [
  {
    icon: "✦",
    title: "Growth & Learning",
    text: "Learn new technologies and continuously develop your professional skills.",
  },
  {
    icon: "◇",
    title: "Innovation",
    text: "Work on exciting projects where your ideas can create real impact.",
  },
  {
    icon: "✣",
    title: "Great Team",
    text: "Collaborate with talented people who support and inspire each other.",
  },
  {
    icon: "◈",
    title: "Meaningful Work",
    text: "Build technology solutions that solve real customer and business problems.",
  },
  {
    icon: "✧",
    title: "Recognition",
    text: "Your contributions matter and your achievements are valued.",
  },
  {
    icon: "○",
    title: "Work-Life Balance",
    text: "We believe great work happens when people have time to recharge.",
  },
];

const Careers = () => {
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("All");
  const [jobType, setJobType] = useState("All");

  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationJob, setApplicationJob] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [resumeClicked, setResumeClicked] = useState(false);
  const [clickedHeroButton, setClickedHeroButton] = useState(null);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const searchText = search.toLowerCase().trim();

      const matchesSearch =
        job.title.toLowerCase().includes(searchText) ||
        job.department.toLowerCase().includes(searchText) ||
        job.skills.some((skill) =>
          skill.toLowerCase().includes(searchText)
        );

      const matchesDepartment =
        department === "All" || job.department === department;

      const matchesType =
        jobType === "All" || job.type === jobType;

      return matchesSearch && matchesDepartment && matchesType;
    });
  }, [search, department, jobType]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const openApplication = (job) => {
    setSelectedJob(null);
    setApplicationJob(job);
    setSubmitted(false);
  };

  const closeApplication = () => {
    setApplicationJob(null);
    setSubmitted(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <main className="careers-page">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="career-hero">
        <div className="career-container career-hero-inner">

          <div className="career-hero-content">

            <span className="hero-badge">
              JOIN THE TECHLYNX TEAM
            </span>

            <h1>
              Build Your Future
            <span>With Techlynx</span>
            </h1>

            <p>
              Join a team of passionate innovators, developers,
              designers and problem-solvers creating technology
              that makes a difference.
            </p>

            <div className="hero-buttons">

              <button
                type="button"
                className={`career-primary-button${clickedHeroButton === "positions" ? " is-clicked" : ""}`}
                onClick={() => {
                  setClickedHeroButton("positions");
                  scrollToSection("open-positions");
                }}
              >
                Explore Open Positions
                <span>→</span>
              </button>

              <button
                type="button"
                className={`career-secondary-button${clickedHeroButton === "why" ? " is-clicked" : ""}`}
                onClick={() => {
                  setClickedHeroButton("why");
                  scrollToSection("why-techlynx");
                }}
              >
                Why Techlynx?
              </button>

            </div>

          </div>

          <div className="career-hero-visual" aria-hidden="true">
            <div className="hero-motion-system hero-signal-system">
              <span className="hero-network-line hero-network-line-one"></span>
              <span className="hero-network-line hero-network-line-two"></span>
              <span className="hero-network-line hero-network-line-three"></span>
              <span className="hero-network-line hero-network-line-four"></span>
              <span className="hero-network-line hero-network-line-five"></span>

              <span className="hero-signal-node hero-signal-node-one"></span>
              <span className="hero-signal-node hero-signal-node-two"></span>
              <span className="hero-signal-node hero-signal-node-three"></span>
              <span className="hero-signal-node hero-signal-node-four"></span>
              <span className="hero-signal-node hero-signal-node-five"></span>
              <span className="hero-signal-node hero-signal-node-six"></span>

              <span className="hero-data-pulse hero-data-pulse-one"></span>
              <span className="hero-data-pulse hero-data-pulse-two"></span>
              <span className="hero-data-pulse hero-data-pulse-three"></span>

              <span className="hero-grid-orbit hero-grid-orbit-one"></span>
              <span className="hero-grid-orbit hero-grid-orbit-two"></span>
              <span className="hero-grid-orbit hero-grid-orbit-three"></span>

              <div className="hero-main-orbit">
                <span className="hero-core-halo"></span>
                <div className="hero-center-card">
                  <span className="hero-core-scan"></span>
                  <strong>TL</strong>
                  <small>INNOVATE</small>
                </div>
              </div>
</div>
          </div>
          </div>
        </section>


      {/* =====================================================
          WHY TECHLYNX
      ===================================================== */}

      <section
        className="why-section"
        id="why-techlynx"
      >

        <div className="career-container">

          <div className="why-intro">

            <div className="why-heading">

              <span className="section-label">
                WHY TECHLYNX
              </span>

              <h2 className="section-title">
                Build work that
               moves you forward
              </h2>

            </div>

            <div className="why-description">

            

              <div className="why-note">
                <span className="why-note-mark">✦</span>
                <span>
                  People-first teams. Ambitious work. Real impact.
                </span>
              </div>

            </div>

          </div>


          <div className="benefits-grid">

            {benefits.map((item) => {
              const isSelected = selectedBenefit === item.title;

              return (
                <article
                  className={`benefit-card${isSelected ? " is-selected" : ""}`}
                  key={item.title}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={() =>
                    setSelectedBenefit(isSelected ? null : item.title)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedBenefit(isSelected ? null : item.title);
                    }
                  }}
                >

                  <div className="benefit-top">

                    <div className="benefit-icon">
                      {item.icon}
                    </div>

                    <span className="benefit-line"></span>

                  </div>

                  <div className="benefit-content">

                    <h3>{item.title}</h3>

                    <p>{item.text}</p>

                  </div>

                </article>
              );
            })}

          </div>

        </div>

      </section>


      {/* =====================================================
          OPEN POSITIONS
      ===================================================== */}

      <section
        className="jobs-section"
        id="open-positions"
      >

        <div className="career-container">

          <div className="jobs-header">

            <div className="jobs-heading">

              <span className="section-label">
                CAREER OPPORTUNITIES
              </span>

              <h2 className="section-title">
                Find your next
                opportunity
              </h2>

              <p className="section-description">
                Explore roles where you can learn, contribute
                and build meaningful technology.
              </p>

            </div>

          </div>


          {/* SEARCH + FILTERS */}

          <div className="search-container">

            <div className="search-field">

              <span
                className="search-icon"
                aria-hidden="true"
              ></span>

              <input
                className="search-input"
                type="text"
                placeholder="Search jobs, skills or departments..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

            </div>


            <select
              className="filter-select"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            >
              <option value="All">
                All Departments
              </option>

              <option value="Engineering">
                Engineering
              </option>

              <option value="Data & Analytics">
                Data & Analytics
              </option>

              <option value="Design">
                Design
              </option>

              <option value="Marketing">
                Marketing
              </option>
            </select>


            <select
              className="filter-select"
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
            >
              <option value="All">
                All Job Types
              </option>

              <option value="Full Time">
                Full Time
              </option>

              <option value="Part Time">
                Part Time
              </option>

              <option value="Internship">
                Internship
              </option>
            </select>

          </div>


          {/* JOB LIST */}

          <div className="jobs-list">

            {filteredJobs.length > 0 ? (

              filteredJobs.map((job) => (

                <article
                  className={`job-card job-card-${job.id}`}
                  key={job.id}
                >

                  <div className="job-top">

                    <div className="company-logo">
                      TL
                    </div>

                    <div className="job-info">

                      <span className="job-department">
                        {job.department}
                      </span>

                      <h3>
                        {job.title}
                      </h3>

                      <div className="job-meta">

                        <span className="job-meta-item">
                          <span className="meta-location"></span>
                          {job.location}
                        </span>

                        <span className="job-meta-item">
                          <span className="meta-work"></span>
                          {job.type}
                        </span>

                        <span className="job-meta-item">
                          <span className="meta-experience"></span>
                          {job.experience}
                        </span>

                      </div>

                    </div>

                  </div>


                  <p className="job-description">
                    {job.description}
                  </p>


                  <div className="skills">

                    {job.skills.map((skill) => (
                      <span
                        className="skill"
                        key={skill}
                      >
                        {skill}
                      </span>
                    ))}

                  </div>


                  <div className="job-bottom">

                    <span className="salary-text">
                      Competitive Salary
                    </span>

                    <button
                      type="button"
                      className="view-button"
                      onClick={() => setSelectedJob(job)}
                    >
                      View Details
                      <span>→</span>
                    </button>

                  </div>

                </article>

              ))

            ) : (

              <div className="no-jobs">

                <div className="no-jobs-icon">
                  <span className="search-icon"></span>
                </div>

                <h3>
                  No positions found
                </h3>

                <p>
                  Try changing your search or filters.
                </p>

              </div>

            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="career-cta">
        <div className="career-container cta-inner">
          <div className="cta-visual cta-signal-system" aria-hidden="true">
            <span className="cta-network-line cta-network-line-one"></span>
            <span className="cta-network-line cta-network-line-two"></span>
            <span className="cta-network-line cta-network-line-three"></span>
            <span className="cta-network-line cta-network-line-four"></span>

            <span className="cta-signal-node cta-signal-node-one"></span>
            <span className="cta-signal-node cta-signal-node-two"></span>
            <span className="cta-signal-node cta-signal-node-three"></span>
            <span className="cta-signal-node cta-signal-node-four"></span>
            <span className="cta-signal-node cta-signal-node-five"></span>

            <span className="cta-data-pulse cta-data-pulse-one"></span>
            <span className="cta-data-pulse cta-data-pulse-two"></span>
            <span className="cta-data-pulse cta-data-pulse-three"></span>

            <span className="cta-orbit cta-orbit-one"></span>
            <span className="cta-orbit cta-orbit-two"></span>
            <span className="cta-orbit cta-orbit-three"></span>
            <span className="cta-center">TL</span>
          </div>

          <div className="cta-content">
            <span className="section-label">DON'T SEE YOUR ROLE?</span>
            <h2>
              We Are Always Looking
              For Great Talent
            </h2>

            <p>
              Haven't found the right role yet? Share your resume with us.
              We’ll keep your profile in mind as new opportunities open.
            </p>

            <button
              type="button"
              className={`career-primary-button${resumeClicked ? " is-clicked" : ""}`}
              onClick={() => {
                setResumeClicked(true);
                window.open(
                  "https://docs.google.com/forms/d/e/1FAIpQLSefkrnodUCHLVVf1r61Z9HJ1XWrMFpI-DJBaqImsviAVD4sDA/viewform?usp=publish-editor",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              Send Your Resume
              <span>→</span>
            </button>
          </div>

        </div>
      </section>


      {/* =====================================================
          JOB DETAILS MODAL
      ===================================================== */}

      {selectedJob && (

        <div
          className="modal-overlay"
          onClick={() => setSelectedJob(null)}
        >

          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >

            <button
              type="button"
              className="close-button"
              onClick={() => setSelectedJob(null)}
            >
              ×
            </button>

            <div className="modal-header">
              <div className="modal-title-area">
                <span className="job-department">
                  {selectedJob.department}
                </span>

                <h2>
                  {selectedJob.title}
                </h2>

                <p className="modal-intro">
                  A clear overview of the role, what you will work on,
                  and the skills that will help you succeed.
                </p>
              </div>

              <div className="modal-role-mark">TL</div>
            </div>

            <div className="modal-meta">

              <span>
                <span className="meta-location"></span>
                {selectedJob.location}
              </span>

              <span>
                <span className="meta-work"></span>
                {selectedJob.type}
              </span>

              <span>
                <span className="meta-experience"></span>
                {selectedJob.experience}
              </span>

            </div>


            <div className="modal-section">

              <h3>
                About the Role
              </h3>

              <p>
                {selectedJob.description}
              </p>

            </div>


            <div className="modal-section">

              <h3>
                Responsibilities
              </h3>

              <ul>
                {selectedJob.responsibilities.map(
                  (item) => (
                    <li key={item}>
                      {item}
                    </li>
                  )
                )}
              </ul>

            </div>


            <div className="modal-section">

              <h3>
                Required Skills
              </h3>

              <div className="skills">

                {selectedJob.skills.map(
                  (skill) => (
                    <span
                      className="skill"
                      key={skill}
                    >
                      {skill}
                    </span>
                  )
                )}

              </div>

            </div>


            <div className="modal-apply-note">
              Ready to take the next step?
            </div>

            <button
              type="button"
              className="career-primary-button modal-apply"
              onClick={() =>
                openApplication(selectedJob)
              }
            >
              Apply for this Position
              <span>→</span>
            </button>

          </div>

        </div>

      )}


      {/* =====================================================
          APPLICATION MODAL
      ===================================================== */}

      {applicationJob && (

        <div
          className="modal-overlay"
          onClick={closeApplication}
        >

          <div
            className="modal application-modal"
            onClick={(e) => e.stopPropagation()}
          >

            <button
              type="button"
              className="close-button"
              onClick={closeApplication}
            >
              ×
            </button>

            {!submitted ? (

              <>

                <span className="section-label">
                  CAREER APPLICATION
                </span>

                <h2>
                  Apply for {applicationJob.title}
                </h2>

                <p className="form-description">
                  Tell us a little about yourself.
                  Our recruitment team will review your
                  application.
                </p>


                <form onSubmit={handleSubmit}>

                  <div className="form-row">

                    <div className="form-group">

                      <label>
                        Full Name *
                      </label>

                      <input
                        className="form-input"
                        type="text"
                        placeholder="Enter your full name"
                        required
                      />

                    </div>


                    <div className="form-group">

                      <label>
                        Email *
                      </label>

                      <input
                        className="form-input"
                        type="email"
                        placeholder="you@example.com"
                        required
                      />

                    </div>

                  </div>


                  <div className="form-row">

                    <div className="form-group">

                      <label>
                        Phone Number *
                      </label>

                      <input
                        className="form-input"
                        type="tel"
                        placeholder="+91 XXXXX XXXXX"
                        required
                      />

                    </div>


                    <div className="form-group">

                      <label>
                        Experience
                      </label>

                      <input
                        className="form-input"
                        type="text"
                        placeholder="Fresher / 1 Year"
                      />

                    </div>

                  </div>


                  <div className="form-group">

                    <label>
                      LinkedIn Profile
                    </label>

                    <input
                      className="form-input"
                      type="url"
                      placeholder="https://linkedin.com/in/yourname"
                    />

                  </div>


                  <div className="form-group">

                    <label>
                      GitHub / Portfolio
                    </label>

                    <input
                      className="form-input"
                      type="url"
                      placeholder="https://github.com/yourname"
                    />

                  </div>


                  <div className="form-group">

                    <label>
                      Resume *
                    </label>

                    <div className="resume-box">

                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        required
                      />

                      <p className="resume-help">
                        Accepted formats: PDF, DOC, DOCX
                      </p>

                    </div>

                  </div>


                  <div className="form-group">

                    <label>
                      Cover Letter
                    </label>

                    <textarea
                      className="form-textarea"
                      rows="5"
                      placeholder="Tell us why you would be a great fit for Techlynx..."
                    />

                  </div>


                  <button
                    type="submit"
                    className="career-primary-button submit-button"
                  >
                    Submit Application
                    <span>→</span>
                  </button>

                </form>

              </>

            ) : (

              <div className="success">

                <div className="success-icon">
                  ✓
                </div>

                <h2>
                  Application Submitted!
                </h2>

                <p>
                  Thank you for applying to Techlynx.
                  Our recruitment team will review your
                  application and contact you if your
                  profile matches our requirements.
                </p>

                <button
                  type="button"
                  className="career-primary-button"
                  onClick={closeApplication}
                >
                  Done
                </button>

              </div>

            )}

          </div>

        </div>

      )}

    </main>
  );
};

export default Careers;